import ApiInstance from './instance'
import {
  EOrigin,
  IAutoPayItem,
  IAutoPayItemFilterResult,
  IAutoPayItemFrontEndFilters,
  IAutoPaySetting,
  IAutoPaySettingsFilterResult,
  ICalculatedResidentAutoApplyPaymentDTO,
  ICalculateScheduledPaymentDetailsDTO,
  ICalculateScheduledPaymentDetailsResultDTO,
  IGetPccDepositListDto,
  IOneTimePaymentDTO,
  IPayment,
  IPaymentsFiltersDTO,
  IPaymentsListResponse,
  IPaymentStatus,
  IPaymentTransactionEffectiveDate,
  IPccDepositResponse,
  IResidentAutoPaySettingFilters,
  IResidentPaymentListFilters,
  IScheduledPayment,
  IScheduleItemsFilterResult,
  IScheduleSettingStatisticsDTO,
  pccDepositPaymentDistribution,
  URL_MAPPING,
  ITransactionToPay,
  UnassignedPccDepositsStatsByUsername,
  IPreferredPayersForPayment
} from '@zunta/configurations'

const { payments: paymentsUrls } = URL_MAPPING

export default class PaymentsApi {
  instance: ApiInstance

  constructor (instance: ApiInstance) {
    this.instance = instance
  }

  async getFacilitiesPaymentsNotInSync (facilityIds: string[] | number[]): Promise<string[]> {
    return this.instance.doGet(`${paymentsUrls.prefix}/facilityPaymentsNotInSync`, { facilityIds })
  }

  getResidentAutoApplyPaymentCalculation (residentId: number | string, amount: number): Promise<ICalculatedResidentAutoApplyPaymentDTO> {
    return this.instance.doPost(`${paymentsUrls.prefix}${paymentsUrls.caclulateResidentAutoApplyPayment.path}/${residentId}`, { amount })
  }

  async runAutoPayForSetting (settingId: number | string): Promise<'OK'> {
    return this.instance.doPost(`${paymentsUrls.prefix}/runAutoPayForSetting/${settingId}`)
  }

  async getPaymentsList (filters: IPaymentsFiltersDTO): Promise<IPaymentsListResponse> {
    return this.instance.doGet(`${paymentsUrls.prefix}${paymentsUrls.paymentsList.path}`, filters)
  }

  async applyDepositToPcc (depositId: number, effectiveDate: string | Date): Promise<'OK'> {
    return this.instance.doPost(`${paymentsUrls.prefix}/applyDepositToPcc/${depositId}`, { effectiveDate })
  }

  async applyDeposit (depositId: number, facilityOrigin: EOrigin): Promise<'OK'> {
    return this.instance.doPost(`${paymentsUrls.prefix}/applyDeposit/${depositId}/${facilityOrigin}`)
  }

  makeOneTimePayment (residentId: number | string, paymentData: IOneTimePaymentDTO): Promise<IPayment> {
    return this.instance.doPost(`${paymentsUrls.prefix}${paymentsUrls.makeOneTimePayment.path}/${residentId}`, paymentData)
  }

  makeHMOPayment (facilityId: number | string, paymentData: IOneTimePaymentDTO): Promise<IPayment> {
    return this.instance.doPost(`/payments/makeHMOPayment`, paymentData)
  }

  getResidentPayment (paymentId: number | string): Promise<IOneTimePaymentDTO> {
    return this.instance.doGet(`${paymentsUrls.prefix}${paymentsUrls.getResidentPayment.path}/${paymentId}`)
  }

  getAutoPayItem (autoPayItemId: number | string): Promise<IAutoPayItem> {
    return this.instance.doGet(`${paymentsUrls.prefix}/autoPayItems/${autoPayItemId}`)
  }

  calculateSchedulePaymentDetails (residentId: number | string, params: ICalculateScheduledPaymentDetailsDTO): Promise<ICalculateScheduledPaymentDetailsResultDTO> {
    return this.instance.doPost(`${paymentsUrls.prefix}${paymentsUrls.calculateScheduledPaymentDetails.path}/${residentId}`, params)
  }

  // saveScheduledPayment (residentId: number | string, data: Partial<ISchedulePaymentSetting>): Promise<ISchedulePaymentSetting> {
  //   return this.instance.doPost(`${paymentsUrls.prefix}${paymentsUrls.saveScheduledPayment.path}/${residentId}`, data)
  // }
  //
  // getScheduledPayment (scheduleId: number | string): Promise<ISchedulePaymentSetting> {
  //   return this.instance.doGet(`${paymentsUrls.prefix}${paymentsUrls.getScheduledPayment.path}/${scheduleId}`)
  // }

  saveAutoPayment (residentId: number | string, data: Partial<IAutoPaySetting>): Promise<IAutoPaySetting> {
    return this.instance.doPost(`${paymentsUrls.prefix}/saveAutoPaySetting/${residentId}`, data)
  }

  getAutoPayment (id: number | string): Promise<IAutoPaySetting> {
    return this.instance.doGet(`${paymentsUrls.prefix}/getAutoPaySetting/${id}`)
  }

  sendPaymentReceiptViaEmail (paymentId: number | string, contact: { name: string, email: string }) {
    return this.instance.doPost(`${paymentsUrls.prefix}${paymentsUrls.sendPaymentReceiptViaEmail.path}/${paymentId}`, contact)
  }

  sendPaymentReceiptViaMail (paymentId: number | string, contact: {
    name: string,
    address: string,
    addressLine1: string,
    addressLine2?: string,
    state: string,
    city: string,
    zip: string
  }) {
    return this.instance.doPost(`${paymentsUrls.prefix}${paymentsUrls.sendPaymentReceiptViaMail.path}/${paymentId}`, contact)
  }

  getResidentPaymentsByFilters (residentId: number | string, filters: IResidentPaymentListFilters): Promise<IPaymentsListResponse> {
    return this.instance.doGet(`${paymentsUrls.prefix}${paymentsUrls.getResidentPaymentsByFilters.path}/${residentId}`, filters)
  }

  getResidentPaymentTransactions (paymentId: number | string): Promise<IPaymentTransactionEffectiveDate[]> {
    return this.instance.doGet(`${paymentsUrls.prefix}${paymentsUrls.getResidentPaymentTransactions.path}/${paymentId}`)
  }

  getResidentRejectTransactions (paymentId: number | string): Promise<any> {
    return this.instance.doGet(`${paymentsUrls.prefix}/getResidentRejectTransactions/${paymentId}`)
  }

  getScheduledPaymentTransactions (paymentId: number | string): Promise<IPaymentTransactionEffectiveDate[]> {
    return this.instance.doGet(`${paymentsUrls.prefix}/getScheduledPaymentTransactions/${paymentId}`)
  }

  getGroupedPaymentsTotals (residentId: number | string): Promise<any> {
    return this.instance.doGet(`${paymentsUrls.prefix}/getGroupedPaymentsTotals/${residentId}`)
  }

  getResidentSchduledPayments (residentId: number | string): Promise<IScheduledPayment[]> {
    return this.instance.doGet(`${paymentsUrls.prefix}${paymentsUrls.getResidentScheduledPayments.path}/${residentId}`)
  }

  getScheduleItemsByFilters (filters: IAutoPayItemFrontEndFilters): Promise<IScheduleItemsFilterResult> {
    return this.instance.doGet(`${paymentsUrls.prefix}${paymentsUrls.getScheduleItemsByFilters.path}`, filters)
  }

  getChainAutoPayItemsByFilters (filters: IAutoPayItemFrontEndFilters): Promise<IAutoPayItemFilterResult> {
    return this.instance.doGet(`${paymentsUrls.prefix}/getChainAutoPayItems`, filters)
  }

  getAutoPayItems (residentId: number | string): Promise<IAutoPayItemFilterResult> {
    return this.instance.doGet(`${paymentsUrls.prefix}/getAutoPayItems/${residentId}`)
  }

  getResidentAutoPaySettings (residentId: number | string, filters?: IResidentAutoPaySettingFilters): Promise<IAutoPaySettingsFilterResult> {
    return this.instance.doGet(`${paymentsUrls.prefix}/getResidentAutoPaySettings/${residentId}`, filters)
  }

  getAutoPaySetting (settingId: number) {
    return this.instance.doGet(`${paymentsUrls.prefix}/getAutoPaySetting/${settingId}`)
  }

  getScheduleItemTransactionsForApply (scheduleItemId: number | string): Promise<ITransactionToPay[]> {
    return this.instance.doGet(`${paymentsUrls.prefix}${paymentsUrls.getScheduleItemTransactionsForApply.path}/${scheduleItemId}`)
  }

  autoPayItemProcess (autoPayItemId: number | string): Promise<any> {
    return this.instance.doPut(`${paymentsUrls.prefix}/autoPayItemProcess/${autoPayItemId}`)
  }

  autoPayNeedsReviewToggle (autoPayItemId: number | string): Promise<any> {
    return this.instance.doPut(`${paymentsUrls.prefix}/autoPayNeedsReviewToggle/${autoPayItemId}`)
  }

  autoPayStop (autoPaySettingId: number | string, stoppedFromReview: boolean = true): Promise<any> {
    return this.instance.doPut(`${paymentsUrls.prefix}/autoPayStop/${autoPaySettingId}/${stoppedFromReview}`)
  }

  autoPayItemSkip (autoPayItemId: number | string): Promise<any> {
    return this.instance.doPut(`${paymentsUrls.prefix}/autoPaySkip/${autoPayItemId}`)
  }

  autoPayItemPostpone (autoPayItemId: number | string, date: Date | string): Promise<any> {
    return this.instance.doPut(`${paymentsUrls.prefix}/autoPayItemPostpone/${autoPayItemId}`, { date })
  }

  scheduleItemChangePaymentMethod (scheduleSettingId: number | string, paymentMethod: number) {
    return this.instance.doPut(`${paymentsUrls.prefix}${paymentsUrls.scheduleItemChangePaymentMethod.path}/${scheduleSettingId}`, { paymentMethod })
  }

  refundPayment (paymentId: number, refundData: any) {
    return this.instance.doPost(`${paymentsUrls.prefix}/refundPayment/${paymentId}`, refundData)
  }

  getScheduleSettingStatistics (scheduleSettingId: number | string): Promise<IScheduleSettingStatisticsDTO> {
    return this.instance.doGet(`${paymentsUrls.prefix}${paymentsUrls.getScheduleSettingStatistics.path}/${scheduleSettingId}`)
  }

  generateReceipt (paymentId: number | string) {
    return this.instance.doGet(`${paymentsUrls.prefix}/paymentReceipt/${paymentId}`)
  }

  editPayment (paymentId: number, residentId: number, transactions: any[]) {
    return this.instance.doPut(`${paymentsUrls.prefix}/editPayment/${residentId}/${paymentId}`, { transactions })
  }

  getDeposits (filters: any) {
    return this.instance.doGet('/deposits/getDepositsList', filters)
  }

  getPccDeposits (filters: IGetPccDepositListDto): Promise<IPccDepositResponse> {
    return this.instance.doGet('/pcc/getPccDepositsList', filters)
  }

  getUnassignedPccDepositsStatsByUsername (): Promise<UnassignedPccDepositsStatsByUsername[]> {
    return this.instance.doGet('/pcc/getUnassignedPccDepositsStatsByUsername')
  }

  assignPccDepositsByPccUsername (username: string): Promise<'OK'> {
    return this.instance.doPatch('/pcc/assignPccDepositsByPccUsername', { username })
  }

  getDepositsTransactions (filters: any) {
    return this.instance.doGet(`${paymentsUrls.prefix}/deposits/transactions`, filters)
  }

  getPaymentStatusById (id: number) {
    return this.instance.doGet(`/payments/getPaymentStatusById/${id}`)
  }

  getBanksList (filters: any) {
    return this.instance.doGet(`/payments/getBankList`, filters)
  }

  exportDeposits (filters: any) {
    this.instance.doGetDownload('deposits/exportDeposits', { filters: JSON.stringify(filters) })
  }

  exportDeposit (deposit: number) {
    this.instance.doGetDownload(`deposits/exportDeposit/${deposit}`)
  }

  downloadDepositForExport (willowId: number) {
    this.instance.doGetDownload(`deposits/downloadDepositForExport/${willowId}`)
  }

  downloadDepositReport (depositId: number, fileName: string) {
    return this.instance.doGetCompressedDownload(`pcc/downloadDepositReport/${depositId}`, {}, fileName)
  }

  assignUserToPccDeposits (depositIds: number[]) {
    return this.instance.doPut(`pcc/assignUserToPccDeposits`, { depositIds })
  }

  getPaymentPayerTypes (facilityId: number) {
    return this.instance.doGet(`/payments/getPaymentPayerTypes/${facilityId}`)
  }

  getPreferredPayersForPayment (paymentId: number): Promise<IPreferredPayersForPayment[]> {
    return this.instance.doGet(`pcc/getPreferredPayersForPayment/${paymentId}`)
  }

  getPccDepositsValidationErrors (depositIds: number[]) {
    return this.instance.doGet(`pcc/getPccDepositsValidationErrors`, { depositIds })
  }

  markPCCDepositAsSync (willowDepositId: number) {
    return this.instance.doPost(`pcc/manuallySyncDeposit/${willowDepositId}`)
  }
}
